import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { cachedGet, invalidateCache } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { EventStatusPill } from "@/components/Shared";
import EventForm, { EMPTY_EVENT } from "@/pages/staff/EventForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, Users, Copy } from "lucide-react";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtLong(d) {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function CapacityChip({ event }) {
  return (
    <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-sm ${event.spaces_remaining === 0 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
      {event.booked}/{event.capacity}
    </span>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManagement = user?.role === "management_admin";
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dupEvent, setDupEvent] = useState(null);
  const [dupDate, setDupDate] = useState("");

  const [error, setError] = useState(false);
  const load = () => {
    setError(false);
    cachedGet("/admin/events", 45000)
      .then((data) => { setEvents(data); setLoading(false); })
      .catch(() => { setLoading(false); setError(true); });
  };
  useEffect(() => { load(); }, []);

  const byDate = useMemo(() => {
    const m = {};
    for (const e of events) (m[e.date] = m[e.date] || []).push(e);
    return m;
  }, [events]);

  const moveCursor = (dir) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCursor(d);
  };

  const monthCells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(first.getDate() - startOffset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  const weekDays = useMemo(() => {
    const d = new Date(cursor);
    const off = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - off);
    return Array.from({ length: 7 }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return x; });
  }, [cursor]);

  const agendaEvents = useMemo(
    () => [...events].sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time)),
    [events]
  );

  const duplicate = async () => {
    if (!dupDate) { toast.error("Pick a date for the copy."); return; }
    try {
      await api.post(`/admin/events/${dupEvent.id}/duplicate`, { date: dupDate });
      invalidateCache("/admin/events");
      toast.success("Assessment day duplicated.");
      setDupEvent(null); setDupDate("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const todayIso = iso(new Date());

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Management</p>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="calendar-heading">
            Assessment Calendar
          </h1>
          <p className="text-sm text-slate-500 mt-1">Schedule assessment days, manage capacity, bookings and attendance.</p>
        </div>
        {isManagement && (
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="new-event-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Assessment Day
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 bg-white border border-steel rounded-md p-1" data-testid="view-switcher">
          {[["month", "Month"], ["week", "Week"], ["agenda", "Agenda"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-4 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest transition-colors ${view === v ? "bg-lomond text-black font-bold" : "text-slate-500 hover:text-charcoal"}`}
              data-testid={`view-${v}`}>
              {l}
            </button>
          ))}
        </div>
        {view !== "agenda" && (
          <div className="flex items-center gap-2 ml-2">
            <Button variant="outline" size="icon" onClick={() => moveCursor(-1)} className="border-steel bg-white h-8 w-8" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="font-heading font-bold uppercase text-charcoal text-sm min-w-40 text-center" data-testid="cal-cursor-label">
              {view === "month" ? `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`
                : `${weekDays[0].toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${weekDays[6].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
            </span>
            <Button variant="outline" size="icon" onClick={() => moveCursor(1)} className="border-steel bg-white h-8 w-8" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())} className="text-slate-500 text-xs" data-testid="cal-today">Today</Button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 rounded-md px-4 py-3 flex items-center justify-between gap-3" data-testid="calendar-error">
          <p className="text-sm text-red-800">Could not load the assessment calendar. Check your connection and try again.</p>
          <Button size="sm" onClick={load} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="calendar-retry-btn">Retry</Button>
        </div>
      )}

      {view === "month" && (
        <div className="bg-white border border-steel rounded-md overflow-hidden" data-testid="month-view">
          <div className="grid grid-cols-7 border-b border-steel">
            {DAY_NAMES.map((d) => <div key={d} className="px-2 py-2 text-[10px] font-mono uppercase tracking-widest text-slate-500 text-center">{d}</div>)}
          </div>
          {loading ? (
            <div className="grid grid-cols-7" data-testid="calendar-loading">
              {Array.from({ length: 42 }).map((_, i) => (
                <div key={i} className="min-h-24 border-b border-r border-steel/60 p-1.5">
                  <div className="h-3 w-6 bg-steel/40 rounded-sm animate-pulse mb-2" />
                  <div className="h-7 bg-steel/25 rounded-sm animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
          <div className="grid grid-cols-7">
            {monthCells.map((d, i) => {
              const key = iso(d);
              const dayEvents = byDate[key] || [];
              const inMonth = d.getMonth() === cursor.getMonth();
              return (
                <div key={i} className={`min-h-24 border-b border-r border-steel/60 p-1.5 ${inMonth ? "bg-white" : "bg-elevated/50"} ${key === todayIso ? "ring-2 ring-inset ring-lomond" : ""}`} data-testid={`day-${key}`}>
                  <p className={`text-xs font-mono mb-1 ${inMonth ? "text-charcoal font-semibold" : "text-slate-400"}`}>{d.getDate()}</p>
                  <div className="space-y-1">
                    {dayEvents.map((e) => (
                      <button key={e.id} onClick={() => navigate(`/staff/calendar/${e.id}`)}
                        className="w-full text-left bg-[#FFF6D6] hover:bg-lomond/40 border border-lomond/50 rounded-sm px-1.5 py-1 transition-colors"
                        data-testid={`event-chip-${e.id}`}>
                        <p className="text-[10px] font-semibold text-charcoal truncate">{e.start_time} {e.event_type === "external_training" ? e.title : "Assessment Day"}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] text-slate-500 truncate">{e.location_name}</span>
                          <CapacityChip event={e} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      {view === "week" && (
        <div className="grid grid-cols-7 gap-2" data-testid="week-view">
          {weekDays.map((d) => {
            const key = iso(d);
            const dayEvents = byDate[key] || [];
            return (
              <div key={key} className={`bg-white border rounded-md min-h-48 p-2 ${key === todayIso ? "border-lomond" : "border-steel"}`} data-testid={`week-day-${key}`}>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{d.toLocaleDateString("en-GB", { weekday: "short" })}</p>
                <p className="font-heading font-bold text-charcoal text-lg mb-2">{d.getDate()}</p>
                <div className="space-y-2">
                  {dayEvents.map((e) => (
                    <button key={e.id} onClick={() => navigate(`/staff/calendar/${e.id}`)}
                      className="w-full text-left bg-[#FFF6D6] hover:bg-lomond/40 border border-lomond/50 rounded-sm p-2 transition-colors"
                      data-testid={`event-chip-${e.id}`}>
                      <p className="text-xs font-semibold text-charcoal">{e.start_time}–{e.end_time}</p>
                      <p className="text-[10px] text-slate-500 truncate">{e.location_name}</p>
                      <CapacityChip event={e} />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "agenda" && (
        <div className="space-y-3" data-testid="agenda-view">
          {agendaEvents.map((e) => (
            <div key={e.id} className="bg-white border border-steel rounded-md p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`agenda-event-${e.id}`}>
              <div className="bg-lomond rounded-md px-3 py-2 text-center min-w-16">
                <p className="font-heading font-black text-black text-xl leading-none">{new Date(e.date + "T00:00:00").getDate()}</p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-black/70">{new Date(e.date + "T00:00:00").toLocaleDateString("en-GB", { month: "short" })}</p>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold uppercase text-charcoal" data-testid={`agenda-title-${e.id}`}>{e.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{fmtLong(e.date)} — {e.start_time}–{e.end_time} — {e.location_name}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <EventStatusPill status={e.effective_status} />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid={`agenda-capacity-${e.id}`}>
                    Capacity {e.capacity} — Booked {e.booked} — Spaces {e.spaces_remaining}
                  </span>
                  {e.waiting > 0 && <span className="font-mono text-[10px] uppercase tracking-widest text-amber-700" data-testid={`agenda-waiting-${e.id}`}>Waiting list: {e.waiting}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isManagement && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setEditing(e); setFormOpen(true); }} className="border-steel text-charcoal bg-white" data-testid={`edit-event-${e.id}`}>Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => setDupEvent(e)} className="border-steel text-charcoal bg-white" data-testid={`dup-event-${e.id}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
                <Button size="sm" onClick={() => navigate(`/staff/calendar/${e.id}`)} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid={`open-event-${e.id}`}>
                  <Users className="h-4 w-4 mr-1.5" /> Manage
                </Button>
              </div>
            </div>
          ))}
          {loading && (
            <div className="space-y-3" data-testid="agenda-loading">
              {[1, 2, 3].map((i) => <div key={i} className="bg-white border border-steel rounded-md h-24 animate-pulse" />)}
            </div>
          )}
          {!loading && !agendaEvents.length && (
            <div className="bg-white border border-steel rounded-md p-10 text-center" data-testid="agenda-empty">
              <CalendarDays className="h-8 w-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No assessment days scheduled yet.</p>
            </div>
          )}
        </div>
      )}

      <EventForm open={formOpen} onClose={() => setFormOpen(false)} event={editing} onSaved={() => { invalidateCache("/admin/events"); load(); }} />

      <Dialog open={!!dupEvent} onOpenChange={() => setDupEvent(null)}>
        <DialogContent className="bg-white border-steel text-foreground" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-charcoal">Duplicate Assessment Day</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Copy "{dupEvent?.title}" to a new date. Bookings are not copied.</p>
          <Input type="date" value={dupDate} onChange={(e) => setDupDate(e.target.value)} className="bg-white border-steel text-charcoal" data-testid="dup-date-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDupEvent(null)} className="border-steel text-charcoal bg-white" data-testid="dup-cancel">Cancel</Button>
            <Button onClick={duplicate} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="dup-confirm">Duplicate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
